import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getRssFeedUrl } from '../../../constants/rss';

export interface NewsHeroTab {
  key: string;
  label: string;
  icon: string;
}

interface NewsHeroProps {
  activeCategory: string;
  onCategoryChange: (cat: string) => void;
  totalCount: number;
  /** 含「全部」+ 与后台栏目 dirname 对应的 Tab（文案已由上层按语言解析） */
  tabs: NewsHeroTab[];
}

export default function NewsHero({ activeCategory, onCategoryChange, totalCount, tabs }: NewsHeroProps) {
  const { t, i18n } = useTranslation();
  const rssUrl = getRssFeedUrl(i18n.language);

  return (
    <section className="relative bg-white pt-[72px] overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <img
          src="http://geel.fp.08wx.cn/images/e3b9c5047ae9aeaa63194bf14f12a115.jpg"
          alt="News Hero"
          className="w-full h-full object-cover object-top opacity-10"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/80 via-white/60 to-white" />
        <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-transparent to-white/95" />
      </div>

      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage: 'linear-gradient(rgba(255,45,120,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,45,120,1) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}
      />

      <div className="relative max-w-[1400px] mx-auto px-6 pt-20 pb-0">
        <div className="flex items-center gap-2 mb-8">
          <Link to="/" className="text-xs text-gray-400 hover:text-primary transition-colors">{t('breadcrumb.home')}</Link>
          <i className="ri-arrow-right-s-line text-gray-300 text-sm" />
          <span className="text-xs text-primary">{t('breadcrumb.news')}</span>
        </div>

        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-10 mb-16">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 bg-accent/10 border border-accent/20 rounded-full">
              <span className="w-2 h-2 rounded-full bg-accent animate-glow-pulse" />
              <span className="text-accent text-xs font-medium uppercase tracking-widest">{t('newsPage.hero.tag')}</span>
            </div>

            <h1 className="text-5xl md:text-6xl font-heading font-black text-gray-900 mb-5 leading-tight">
              {t('newsPage.hero.title')}
              <br />
              <span className="gradient-text">{t('newsPage.hero.highlight')}</span>
            </h1>

            <p className="text-gray-500 text-lg leading-relaxed">
              {t('newsPage.hero.desc1')}
              <br className="hidden sm:block" />
              {t('newsPage.hero.desc2')}
            </p>

            <a
              href={rssUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-white border border-primary/20 rounded-lg text-sm text-gray-600 hover:text-primary hover:border-primary/40 transition-colors"
            >
              <i className="ri-rss-line text-primary" />
              {t('rss.subscribe')}
            </a>
          </div>

          <div className="flex gap-8 lg:pb-2">
            {[
              { value: totalCount.toString(), label: t('newsPage.hero.stats.articles') },
              {
                value: Math.max(0, tabs.filter((x) => x.key !== 'all').length).toString(),
                label: t('newsPage.hero.stats.categories'),
              },
              { value: t('newsPage.hero.stats.frequency'), label: t('newsPage.hero.stats.monthly') },
            ].map((s, i) => (
              <div key={i} className="text-center">
                <div className="text-2xl font-heading font-bold text-primary">{s.value}</div>
                <div className="text-xs text-gray-400 mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2 border-b border-primary/10">
          {tabs.map((cat) => (
            <button
              key={cat.key}
              type="button"
              onClick={() => onCategoryChange(cat.key)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-medium transition-all duration-200 border-b-2 -mb-px cursor-pointer whitespace-nowrap ${
                activeCategory === cat.key ? 'text-primary border-primary' : 'text-gray-400 border-transparent hover:text-gray-600'
              }`}
            >
              <div className="w-4 h-4 flex items-center justify-center"><i className={`${cat.icon} text-sm`} /></div>
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-white to-transparent pointer-events-none" />
    </section>
  );
}
